import { HttpClient, HttpContext, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { HANDLE_ERROR_AND_THROW } from '@helios/utils'
import { Observable, of } from 'rxjs'
import { map } from 'rxjs/operators'
import { rowsFromApi } from '../grid/row.adapter'
import {
  FlexPositionByIdsRequest,
  FlexPositionsQueryRequest,
  FlexPositionSource,
  GridResponse,
} from '../models/flex-positions-query.model'
import { SourceKey } from '../models/source-key.model'
import { WorkbenchRow } from '../models/workbench-row.model'
import {
  WorkbenchDataSource,
  WorkbenchQueryResult,
} from './workbench-data-source'

/**
 * The single Flex Positions query endpoint. Resolved against `<base href="/helios/">`; the
 * `helios-flex-positions` proxy rule (proxy.conf.beta.json for local dev, the nginx location block
 * in prod) rewrites this onto the real backend's `/flex-positions/query`. Clearwater has no
 * endpoint.
 */
export const QUERY_URL = 'helios-flex-positions/flex-positions/query'

/**
 * The by-ids read endpoint, used for the targeted refresh after a match action rather than
 * re-querying a whole block. Same proxy prefix and same `GridResponse` shape as {@link QUERY_URL};
 * verified live on helios@master (FlexPositionResource).
 */
export const BY_IDS_URL = 'helios-flex-positions/flex-positions/by-ids'

/**
 * Positions' SourceKey -> the backend's FlexPositionSource, now sent as the `?source=` QUERY
 * PARAMETER rather than in `context.source` (PVAL-26116). A missing or unknown value is a 400, so
 * this map is the only place a SourceKey becomes one and an unmapped key short-circuits before any
 * request is issued. Clearwater is intentionally absent — it has no read endpoint.
 */
export const QUERY_SOURCE: Partial<Record<SourceKey, FlexPositionSource>> = {
  api: 'ENFUSION',
  prom: 'PROMETHEUS',
}

@Injectable()
export class HttpWorkbenchDataSource extends WorkbenchDataSource {
  constructor(private readonly http: HttpClient) {
    super()
  }

  query(
    source: SourceKey,
    request: FlexPositionsQueryRequest,
  ): Observable<WorkbenchQueryResult> {
    const querySource = QUERY_SOURCE[source]
    // Clearwater has no read endpoint. `lastRow: 0` rather than `undefined` so the grid settles on a
    // known-empty result instead of scrolling for a block that will never come.
    if (!querySource) {
      return of<WorkbenchQueryResult>({ rows: [], lastRow: 0 })
    }

    return this.http
      .post<GridResponse>(QUERY_URL, request, {
        params: new HttpParams().set('source', querySource),
        // Without this, the app-wide ErrorInterceptor swallows a failed request into `EMPTY` — no
        // `next`, no `error`, the Observable just completes with nothing. That is what left the grid
        // stuck on ag-Grid's "Loading" placeholder forever on a 400/500: this component's own error
        // handling (classifyQueryError, the Cache Load Error card, resolving the block) never ran,
        // because it was never reached at all.
        context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
      })
      .pipe(
        map(response => {
          const data = response?.data ?? []
          return {
            rows: rowsFromApi(source, data),
            lastRow: lastRowOf(request, data.length),
          }
        }),
      )
  }

  /**
   * Re-read exactly the named rows. Short-circuits without a request for Clearwater (no read
   * endpoint) and for an empty id list, so an update event that names nothing this grid holds costs
   * nothing.
   */
  loadByIds(source: SourceKey, rowIds: string[]): Observable<WorkbenchRow[]> {
    const querySource = QUERY_SOURCE[source]
    if (!querySource || !rowIds.length) return of<WorkbenchRow[]>([])

    const body: FlexPositionByIdsRequest = {
      requests: [{ source: querySource, rowIds }],
    }

    return this.http
      .post<GridResponse>(BY_IDS_URL, body)
      .pipe(map(response => rowsFromApi(source, response?.data ?? [])))
  }
}

/**
 * The true end index, or `undefined` while it is still unknown.
 *
 * A block that comes back SHORTER than the window that was asked for is the only signal the contract
 * gives that the result set has ended — there is no total in the response. A full block therefore
 * resolves to `undefined` (ag-Grid's "unknown", which it treats as -1) and the grid keeps fetching as
 * the user scrolls; this is what removes the old single-fixed-page truncation.
 *
 * Exported for the spec: the off-by-one here (`startRow + length`, not `endRow`) is what makes the
 * grid's own row count and the status bar's "loaded rows" figure agree with reality.
 */
export function lastRowOf(
  request: FlexPositionsQueryRequest,
  returned: number,
): number | undefined {
  const requested = request.endRow - request.startRow
  if (requested > 0 && returned >= requested) return undefined
  return request.startRow + returned
}
